// modules/website/components/EmbedComponent.tsx - Embed de YouTube, Vimeo, Google Maps

import React from 'react';
import { EmbedData } from '@/types';

interface Props {
  data: EmbedData;
  settings?: any;
}

export const EmbedComponent: React.FC<Props> = ({ data, settings }) => {
  const {
    type = 'youtube',
    embedCode,
    url,
    mapAddress,
    aspectRatio = '16:9',
    title
  } = data || {};

  // Aspect ratio classes
  const aspectClasses: Record<string, string> = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square',
    'auto': ''
  };

  // Extrair ID do YouTube
  const getYoutubeId = (urlStr: string): string => {
    const match = urlStr.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    return match ? match[1] : urlStr;
  };

  // Extrair ID do Vimeo
  const getVimeoId = (urlStr: string): string => {
    const match = urlStr.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : urlStr;
  };

  const renderEmbed = () => {
    switch (type) {
      case 'youtube':
        const ytId = url ? getYoutubeId(url) : '';
        return (
          <iframe
            src={`https://www.youtube.com/embed/${ytId}`}
            title={title || 'Vídeo do YouTube'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded-lg"
          />
        );

      case 'vimeo':
        const vimeoId = url ? getVimeoId(url) : '';
        return (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}`}
            title={title || 'Vídeo do Vimeo'}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded-lg"
          />
        );

      case 'google_maps':
        const encodedAddress = encodeURIComponent(mapAddress || '');
        return (
          <iframe
            src={`https://www.google.com/maps?q=${encodedAddress}&output=embed`}
            title={title || 'Mapa'}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full rounded-lg"
          />
        );

      case 'custom':
        return (
          <div 
            dangerouslySetInnerHTML={{ __html: embedCode || '' }}
            className="w-full h-full"
          />
        );

      default:
        return <div className="text-gray-500 text-center py-8">Tipo de embed não suportado</div>;
    }
  };

  return (
    <div 
      className="py-8 px-6"
      style={{ backgroundColor: settings?.backgroundColor }}
    >
      <div className="max-w-4xl mx-auto">
        {title && (
          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {title}
          </h3>
        )}
        <div className={`${aspectClasses[aspectRatio]} bg-gray-100 rounded-lg overflow-hidden shadow-lg`}>
          {renderEmbed()}
        </div>
      </div>
    </div>
  );
};
